/**
 * Landing Page Utilities
 * 
 * Helper functions for landing page functionality including:
 * - Deep link URL generation
 * - Configuration validation
 * - Analytics event emission
 */

import type { DeepLinkParams, PageConfig, ExampleTrack } from '@/types/landingPage';

/**
 * Generate a deep link URL for the composer
 * 
 * @param params - Deep link parameters
 * @returns Complete URL with query parameters
 */
export function generateDeepLink(params: DeepLinkParams): string {
  const searchParams = new URLSearchParams();

  // Add template parameters
  if (params.template) {
    searchParams.set('template', params.template);
  }

  // Add remix parameter (takes priority over template)
  if (params.remix) {
    searchParams.set('remix', params.remix);
  }

  // Add BPM parameter
  if (params.bpm !== undefined) {
    searchParams.set('bpm', params.bpm.toString());
  }

  // Add key parameter
  if (params.key) {
    searchParams.set('key', params.key);
  }

  // Add UTM parameters for attribution
  if (params.utm_source) {
    searchParams.set('utm_source', params.utm_source);
  }
  if (params.utm_campaign) {
    searchParams.set('utm_campaign', params.utm_campaign);
  }
  if (params.utm_medium) {
    searchParams.set('utm_medium', params.utm_medium);
  }

  const queryString = searchParams.toString();
  return queryString ? `/compose?${queryString}` : '/compose';
}

/**
 * Generate a remix deep link for an example track
 * 
 * @param track - Example track to remix
 * @param pageSlug - Landing page slug for UTM campaign
 * @returns Deep link URL
 */
export function generateRemixLink(track: ExampleTrack, pageSlug: string): string {
  return generateDeepLink({
    remix: track.trackId,
    utm_source: 'seo',
    utm_medium: 'landing-page',
    utm_campaign: pageSlug,
  });
}

/**
 * Generate a template deep link from page configuration
 * 
 * @param config - Page configuration
 * @returns Deep link URL
 */
export function generateTemplateLink(config: PageConfig): string {
  const params: DeepLinkParams = {
    utm_source: 'seo',
    utm_medium: 'landing-page',
    utm_campaign: config.slug,
  };

  if (config.templateDefaults?.template) {
    params.template = config.templateDefaults.template;
  }
  if (config.templateDefaults?.bpm) {
    params.bpm = config.templateDefaults.bpm;
  }
  if (config.templateDefaults?.key) {
    params.key = config.templateDefaults.key;
  }

  return generateDeepLink(params);
}

/**
 * Validate a page configuration object
 * 
 * @param config - Page configuration to validate
 * @returns Array of validation error messages (empty if valid)
 */
export function validatePageConfig(config: PageConfig): string[] {
  const errors: string[] = [];

  // Validate slug format (lowercase, hyphens only)
  if (!/^[a-z0-9-]+$/.test(config.slug)) {
    errors.push(`Invalid slug format: "${config.slug}". Must contain only lowercase letters, numbers, and hyphens.`);
  }

  // Validate title length (50-60 chars recommended)
  if (config.title.length < 40 || config.title.length > 70) {
    errors.push(`Title length (${config.title.length}) should be between 40-70 characters for optimal SEO.`);
  }

  // Validate meta description length (150-160 chars recommended)
  if (config.metaDescription.length < 140 || config.metaDescription.length > 170) {
    errors.push(`Meta description length (${config.metaDescription.length}) should be between 140-170 characters for optimal SEO.`);
  }

  // Validate example tracks array (6-12 items)
  if (config.exampleTracks.length < 6 || config.exampleTracks.length > 12) {
    errors.push(`Example tracks array must contain 6-12 items. Found: ${config.exampleTracks.length}`);
  }

  // Validate FAQ array (minimum 6 items)
  if (config.faq.length < 6) {
    errors.push(`FAQ array must contain at least 6 items. Found: ${config.faq.length}`);
  }

  // Validate required fields are present
  if (!config.h1 || config.h1.trim().length === 0) {
    errors.push('H1 headline is required.');
  }

  if (!config.heroCopy || config.heroCopy.trim().length === 0) {
    errors.push('Hero copy is required.');
  }

  if (!config.ogImage || config.ogImage.trim().length === 0) {
    errors.push('OpenGraph image URL is required.');
  }

  // Validate related pages array
  if (config.relatedPages.length < 2) {
    errors.push(`Related pages array should contain at least 2 items. Found: ${config.relatedPages.length}`);
  }

  // Validate required FAQ topics
  const requiredTopics = [
    'commercial',
    'attribution',
    'export',
    'account',
    'credit',
    'remix',
  ];

  const faqText = config.faq.map(item => 
    `${item.question.toLowerCase()} ${item.answer.toLowerCase()}`
  ).join(' ');

  const missingTopics = requiredTopics.filter(topic => !faqText.includes(topic));
  if (missingTopics.length > 0) {
    errors.push(`FAQ section should cover these topics: ${missingTopics.join(', ')}`);
  }

  return errors;
}

/**
 * Parse deep link parameters from URL search params
 * 
 * @param searchParams - URLSearchParams object
 * @returns Parsed deep link parameters
 */
export function parseDeepLinkParams(searchParams: URLSearchParams): DeepLinkParams {
  const params: DeepLinkParams = {};

  const template = searchParams.get('template');
  if (template) params.template = template;

  const remix = searchParams.get('remix');
  if (remix) params.remix = remix;

  const bpm = searchParams.get('bpm');
  if (bpm) {
    const bpmNum = parseInt(bpm, 10);
    if (!isNaN(bpmNum) && bpmNum > 0 && bpmNum <= 300) {
      params.bpm = bpmNum;
    }
  }

  const key = searchParams.get('key');
  if (key) params.key = key;

  const utmSource = searchParams.get('utm_source');
  if (utmSource) params.utm_source = utmSource;

  const utmCampaign = searchParams.get('utm_campaign');
  if (utmCampaign) params.utm_campaign = utmCampaign;

  const utmMedium = searchParams.get('utm_medium');
  if (utmMedium) params.utm_medium = utmMedium;

  return params;
}

/**
 * Check if a slug is valid for a landing page URL
 * 
 * @param slug - Slug to validate
 * @returns True if valid, false otherwise
 */
export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9-]+$/.test(slug) && slug.length > 0 && slug.length <= 100;
}
